export function formatCash(value) {
  if (!value) return '0,00';

  const number = parseFloat(value);
  if (isNaN(number)) return '0,00';

  const fixed = number.toFixed(2);
  let [integerPart, decimalPart] = fixed.split('.');

  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return `${integerPart},${decimalPart}`;
};

export function calculateProjectDurationFlexible(startDateStr, endDateStr) {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    const msInDay = 24 * 60 * 60 * 1000;
    const totalDays = Math.floor((endDate - startDate) / msInDay);

    if (totalDays >= 365) {
        const years = Math.floor(totalDays / 365);
        return `${years} year${years > 1 ? 's' : ''}`;
    } else if (totalDays >= 30) {
        const months = Math.floor(totalDays / 30);
        return `${months} month${months > 1 ? 's' : ''}`;
    } else if (totalDays >= 7) {
        const weeks = Math.floor(totalDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''}`;
    } else {
        return `${totalDays} day${totalDays > 1 ? 's' : ''}`;
    }
}

export function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

export const handlePagination = ({
  action,             
  pagination,       
  isFilterOn,         
  yearSelected,      
  searchTerm,         
  serverFilterProject, 
  getBriefs           
}) => {
  if (action === "next" && pagination.hasMore) {
    if (isFilterOn) {
      serverFilterProject(yearSelected, searchTerm, pagination.nextCursor, "lastId");
    } else {
      getBriefs(pagination.nextCursor, "lastId");
    }
  } else if (action === "prev" && pagination.currentPage > 1) {
    if (isFilterOn) {
      serverFilterProject(yearSelected, searchTerm, pagination.prevCursor, "prevId");
    } else {
      getBriefs(pagination.prevCursor, "prevId");
    }
  }
};

export function formatDate(dateString) {
  if (!dateString) return 'DD/MM/YYYY';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) throw new Error("Invalid Date");

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();

    return `${day}/${month}/${year}`;
  }
}

export function formatDateToMonthYear(dateString) {
  if (!dateString) return 'Month YYYY';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) throw new Error('Invalid Date');

    const options = { year: 'numeric', month: 'long' };
    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Month-Year formatting error:', error);

    const today = new Date();
    const options = { year: 'numeric', month: 'long' };
    return today.toLocaleString('en-US', options);
  }
}

// ---------- FINANCIAL CALCULATIONS ----------

export function calcProfessionalFee(billables) {
  if (!Array.isArray(billables)) return 0;
  return billables.reduce((total, item) => {
    return total + ((item?.unitPrice??0)*(item?.quantity??0));
  }, 0);
}

export function findSelectedProfessional(items) {
  if (!Array.isArray(items)) return null;

  return items.find(item => item.status === "SELECTED") || null;
}

export function findQuoteByProfessional(quotes, selectedProfessionalId) {
  if (!Array.isArray(quotes) || !selectedProfessionalId) return null;
  return quotes.find(quote => quote.professional === selectedProfessionalId) || null;
}

export function calcTotalWithTax(amount, taxRate = 0.12) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcTotalWithTax:", amount);
    return formatCash(0);
  }

  const total = amount + (amount * taxRate);
  return total;
}

export function calcPlatformFee(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcProfessionalFee:", amount);
    return formatCash(0);
  }
  return amount * 0.12;
}

export function calcClientPlatformFee(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcClientPlatformFee:", amount);
    return formatCash(0);
  }

  return amount * 0.15; // Client fee (same % for now)
}

export function calcSponsorServiceFee(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcSponsorServiceFee:", amount);
    return formatCash(0);
  }

  return amount * 0.15; 
}

export function calcSponsorVat(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcSponsorVat:", amount);
    return formatCash(0);
  }

  const professionalFee = amount;
  const sponsorFee = calcSponsorServiceFee(amount); // 15% of amount
  const vat = 0.15 * (professionalFee + sponsorFee);

  return vat;
}

export function calcClientVat(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcSponsorVat:", amount);
    return formatCash(0);
  }

  const professionalFee = amount;
  const platformFee = calcPlatformFee(amount); // 15% of amount
  const vat = 0.15 * (professionalFee + platformFee);

  return vat;
}

export function calcTotalPayableBySponsor(amount) {
  if (typeof amount !== 'number' || isNaN(amount)) {
    console.warn("Invalid amount in calcTotalPayableBySponsor:", amount);
    return formatCash(0);
  }

  const professionalFee = amount;
  const sponsorFee = calcSponsorServiceFee(amount); 
  const vat = calcSponsorVat(amount); 

  return professionalFee + sponsorFee + vat;
}

export const financialCalculations = (TotalDeliverableCost, Brief) => {
  if (Brief?.isMigrated === true) {
    const sv = Brief.staticValues || {};
    return {
      CredFee10: 0, 
      voucherAmntDebited: 0,
      CredFee15: 0,
      TotAmtPay2Prof: sv.professionalFees ?? 0, 
      SponsorSubTot: 0,
      Remaining2BPayByClient: sv.jobAmount ?? 0,
      VoucherRemaininBal: 0,
      ClientSubTot: sv.jobAmount ?? 0, 
      CredFee12: sv.credippleFee ?? 0  
    };
  }

  // --- HANDLE LIVE DATA (Your existing logic) ---
  const totalPayable = 
    Brief?.briefId?.quote?.[0]?.billableTotal ?? 
    Brief?.quote?.[0]?.billableTotal ?? 
    Brief?.payments?.serviceFeeExlVat ?? 
    0;

  const CredFee10 = totalPayable <= 2500 ? 250 : totalPayable * 0.1;
  
  const CredFee12 = totalPayable <= 2500 ? 250 : (
    Brief?.briefId?.quote?.[0]?.credippleClientFee ?? 
    Brief?.quote?.[0]?.credippleClientFee ?? 
    Brief?.payments?.credippleFeeInRand ?? 
    0
  );
  let voucherAmntDebited = 0;
  if (Brief?.tempVoucherObj?.sponsorAmount) {
    const vouchrAmnt = Brief.tempVoucherObj.sponsorAmount;
    voucherAmntDebited = Math.min(vouchrAmnt, TotalDeliverableCost || 0);
  }

  const CredFee15 = voucherAmntDebited * 0.15;
  const TotAmtPay2Prof = totalPayable - CredFee10;
  const SponsorSubTot = CredFee15 + voucherAmntDebited;
  const Remaining2BPayByClient = totalPayable - voucherAmntDebited;
  const VoucherRemaininBal = (Brief?.tempVoucherObj?.sponsorAmount || 0) - voucherAmntDebited;
  const ClientSubTot = Remaining2BPayByClient * (totalPayable <= 2500 ? 1 : 1.12);

  return {
    CredFee10,
    voucherAmntDebited,
    CredFee15,
    TotAmtPay2Prof,
    SponsorSubTot,
    Remaining2BPayByClient,
    VoucherRemaininBal,
    ClientSubTot,
    CredFee12
  };
};

// ---------- PROFESSIONAL / QUOTE HELPERS ----------

function isMatchingProfessional(professionalId, selectedPro) {
  return professionalId === selectedPro?.id || professionalId === selectedPro?._id;
}

export const processQuote = (Brief, SelectedPro) => {
  if (Brief?.isMigrated === true) {
    return { 
      totalPayable: Brief.staticValues?.professionalFees ?? 0, 
      validBillables: [] 
    };
  }

  const quote = Brief?.briefId?.quote ?? Brief?.quote ?? [];

  if (!Array.isArray(quote)) {
    return { totalPayable: 0, validBillables: [] };
  }

  const validBillables = quote
    .filter(q => isMatchingProfessional(q?.professional, SelectedPro))
    .flatMap(q => q.billables || [])
    .filter(Boolean);

  const totalPayable = quote[0]?.billableTotal ?? 0;

  return { totalPayable, validBillables };
};

// ---------- PROFESSIONALS DATA ----------

export const processProfessionalsData = (pros, validProjects, validBriefs) => {
  let professionals = pros.map(pro => {
    const userProjects = validProjects.filter(project => {
      if (Array.isArray(project.projectPlan)) {
        return project.projectPlan.some(plan => plan.professional === pro._id);
      }
      return project.projectPlan?.professional === pro._id;
    });

    const userBriefs = validBriefs.filter(job => {
      const professional = job.projectPlan?.[0]?.professional;
      return (typeof professional === 'object' ? professional?._id : professional) === pro._id;
    });

    const evaluationsPerUser = validProjects
      .filter(project => Array.isArray(project.projectPlan)
        ? project.projectPlan.some(plan => plan.professional === pro._id)
        : project.projectPlan?.professional === pro._id)
      .map(project => project.evaluation);

    return { ...pro, projects: userProjects, briefs: userBriefs, evaluations: evaluationsPerUser, jobCount: userProjects.length };
  });

  professionals.forEach(pro => {
    let totalRating = 0, ratingCount = 0, totalAmount = 0, briefAmount = 0, projectAmount = 0;

    pro.evaluations?.forEach(evaluation => {
      const rating = evaluation?.professionalEvaluation?.summary?.credipple_rating;
      if (rating !== null && rating !== undefined) {
        totalRating += rating;
        ratingCount++;
      }
    });

    pro.projects.forEach(project => { if (project.budgetEstimation) projectAmount += project.budgetEstimation; });
    pro.briefs.forEach(brief => { if (brief.budgetEstimation) briefAmount += brief.budgetEstimation; });

    totalAmount = projectAmount + briefAmount;
    pro.totalAmount = formatCash(totalAmount);
    pro.averageRating = ratingCount > 0 ? totalRating / ratingCount : 'Not Assigned';
  });

  return professionals.sort((a, b) => {
    const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
    const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

// ---------- PROJECTS DATA ----------

export const processProjectsData = (confirmedProjects, projects) => {
  if (!Array.isArray(confirmedProjects) || confirmedProjects.length === 0) return [];

  const briefs = confirmedProjects.filter(item => Array.isArray(item.projectPlan));
  briefs.forEach(brief => enrichBriefWithData(brief, projects));

  const validBriefs = briefs.filter(item => Array.isArray(item.projectPlan) && item.projectPlan.length > 0 && typeof item.projectPlan[0] === 'object');

  return validBriefs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

// ---------- FETCH PROS & LEADS ----------

export const fetchProsAndLeads = async (pros) => {
  try {
    if (!Array.isArray(pros)) pros = [];
    const professionals = [], leads = [], clients = [];
    pros = pros.filter(item => item.roles?.length > 0);

    pros.forEach(pro => {
      if (pro.roles.includes(1988)) professionals.push(pro);
      else if (pro.roles.includes(1987)) leads.push(pro);
      else if (pro.roles.includes(1989)) clients.push(pro);
    });

    return { Leads: leads, Pros: professionals, Clients: clients };
  } catch (e) {
    console.error('Error fetching professionals and leads:', e);
    return { Leads: [], Pros: [], Clients: [] };
  }
};

// ---------- USER JOBS ----------

export const getUserJobs = (Users, validProjects, validBriefs) => {
  return Users.map(user => {
    let userProjects = [], userBriefs = [];

    if (user.roles[0] === 1989) {
      userProjects = validProjects.filter(project => project.client?._id === user._id);
      userBriefs = validBriefs.filter(brief => brief.client?._id === user._id);
    } else if (user.roles[0] === 1987) {
      userProjects = validProjects.filter(project => project.talentPartner?._id === user._id);
      userBriefs = validBriefs.filter(brief => brief.talentPartner?._id === user._id);
    } else {
      userBriefs = validBriefs.filter(brief => Array.isArray(brief.projectPlan) && brief.projectPlan[0]?.professional === user._id);
      userProjects = validProjects.filter(project => Array.isArray(project.projectPlan)
        ? project.projectPlan.some(plan => plan.professional === user._id)
        : project.projectPlan?.professional === user._id
      );
    }

    return { ...user, projects: userProjects ?? [], briefs: userBriefs ?? [], jobCount: userProjects.length ?? 0 };
  });
};

export const truncateString = (str, num) => {
    if (str.length <= num) {
        return str;
    }
    return str.slice(0, num) + '...';
};


export const prepareBriefForUpdate = (brief) => {
  // Remove unnecessary properties
  delete brief.evaluation;
  delete brief.project;

  // Handle client property
  if (brief.client && typeof brief.client === 'object') {
    delete brief.client.briefs;
    delete brief.client.projects;
    brief.client = brief.client._id;
  } else {
    delete brief.client;
  }

  // Handle talentPartner property
  if (brief.talentPartner && typeof brief.talentPartner === 'object') {
    delete brief.talentPartner.briefs;
    delete brief.talentPartner.projects;
    brief.talentPartner = brief.talentPartner._id;
  } else {
    brief.talentPartner = '';
    delete brief.talentPartner;
  }

  // Handle professional in projectPlan
  if (brief.projectPlan?.[0]?.professional && typeof brief.projectPlan[0].professional === 'object') {
    brief.projectPlan[0].professional = brief.projectPlan[0].professional._id;
  }

  //handle shortlist
  if (Array.isArray(brief.shortlist)) {
    brief.shortlist = brief.shortlist.map(item => {
      return {...item, professional: item?.professional?._id??""};
    });
  }

  // Handle quote array
  // if (Array.isArray(brief.quote)) {
  //   brief.quote = brief.quote.map(item => (item && typeof item === 'object' ? item._id : item));
  // }

  // Reset serviceCategory
  brief.serviceCategory = '';

  return brief;
};

// ---------- PROCESS PROJECTS & BRIEFS ----------

export const processProjectsAndBriefs = (validProjects, validBriefs, leads, evaluation, clients, projectPlans) => {
  let processedProjects = validProjects.map(project => {
    const brief = validBriefs.find(b => b._id === project.brief || b._id === project.briefId);
    const lead = leads.find(l => l._id === project.fulfillmentLead || l._id === project.fullfilmentLead) || { firstName: 'Not Assigned', lastName: '', fullName: 'Not Assigned' };

    if (brief) {
      return {
        ...project,
        brief,
        budgetEstimation: brief?.budgetEstimation || 0,
        paymentMethod: brief?.paymentMethod || '',
        voucherNumber: brief?.voucherNumber || '',
        description: brief?.briefDescription || '',
        briefStatus: project.status || 'Not Started',
        quote: brief?.quote || [],
        meetings: brief?.meetings || [],
        shortlist: brief?.shortlist || [],
        scheduledCallNotes: brief?.scheduledCallNotes || [],
        scheduledCalls: brief?.scheduledCalls || [],
        evaluation: evaluation.find(e => e._id === project.evaluation),
        fulfillmentLead: lead,
        skillsOfProfessional: brief?.skillsOfProfessional || [],
        client: clients.find(c => c._id === project.client),
        projectPlan: brief.projectPlan ?? [{
          projEndDate: new Date().toISOString().split('T')[0],
          projStartDate: new Date().toISOString().split('T')[0],
        }]
      };
    }
    return null; 
  }).filter(Boolean);

  processedProjects.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const updatedValidBriefs = validBriefs.map((item) => {
    const newItem = { ...item };
    if (!Array.isArray(newItem.projectPlan) || newItem.projectPlan.length === 0) newItem.projectPlan = '';
    if (newItem.projectPlan === null) newItem.projectPlan = '';

    if (Array.isArray(item.projectPlan) && typeof item.projectPlan[0] === 'object' && '_id' in item.projectPlan[0]) {
      item.projectPlan.push(projectPlans.find(p => p._id === item.projectPlan[0]._id));
    }

    newItem.projectPlan = [projectPlans.find(p => p._id === newItem.projectPlan[0])];
    return newItem;
  });

  updatedValidBriefs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return { processedProjects, updatedValidBriefs };
};

// ---------- HELPERS ----------

export const enrichBriefWithData = (brief, projects) => {
  const filteredProjects = projects.filter(item => item.brief !== undefined);
  const project = filteredProjects.find(item => item.brief === brief._id);

  if (project) {
    brief.evaluation = project.evaluation;
    brief.project = project;
  }

  brief.serviceCategory = brief.typeOfProfessional ?? [];
};

export const truncateText = (text, limit) => {
  if (text?.length <= limit) return text;
  return text.substring(0, limit) + '...';
};
